import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { LOCALE_DE } from '@rechnungen/shared';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Initialen aus einem vollen Namen (BL-150) — geteilt zwischen Top-Bar,
 * Desktop-Sidebar-User-Block und Mobile-Menü, damit alle drei dieselbe
 * Identitäts-Darstellung zeigen statt teils Initialen, teils eines
 * generischen Personen-Icons.
 */
export function initials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]![0] : '';
  return (first + last).toUpperCase() || '?';
}

/**
 * Startet einen Datei-Download über einen unsichtbaren Anchor-Klick
 * (BL-110) — Cookies/CSRF greifen automatisch, kein `api.get`-Umweg nötig,
 * weil die Response ein Buffer statt JSON ist. Bisher dreimal separat in
 * `orderbuch-list.tsx` inline geschrieben (Export/Bulk-Excel/Bulk-DATEV) —
 * neue Export-Buttons (Gutschriften, Wiederkehrend) nutzen diesen Helper
 * statt es ein viertes Mal zu kopieren.
 */
export function downloadFile(url: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function formatCurrency(amount: number, currency = 'EUR', locale = LOCALE_DE): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatDate(date: string | Date | null | undefined, locale = LOCALE_DE): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/**
 * UI-Test U19 (05.05.2026): Datumsbereich-Formatter, der offene Enden
 * lesbar macht statt "01.02.2026 – —". Use-Case: Projekt-Laufzeiten,
 * Vertragslaufzeiten, alles wo eines der zwei Daten fehlen kann.
 *
 *  - Beide gesetzt → "01.02.2026 – 31.12.2026"
 *  - Nur Start    → "ab 01.02.2026"
 *  - Nur Ende     → "bis 31.12.2026"
 *  - Beide leer   → "—"
 */
export function formatDateRange(
  from: string | Date | null | undefined,
  to: string | Date | null | undefined,
  locale = LOCALE_DE,
): string {
  const hasFrom = !!from;
  const hasTo = !!to;
  if (!hasFrom && !hasTo) return '—';
  if (hasFrom && !hasTo) return `ab ${formatDate(from, locale)}`;
  if (!hasFrom && hasTo) return `bis ${formatDate(to, locale)}`;
  return `${formatDate(from, locale)} – ${formatDate(to, locale)}`;
}

export function formatDateTime(date: string | Date | null | undefined, locale = LOCALE_DE): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Relative-Zeit-Formatter ("vor 2 h", "vor 5 min", "gerade eben", "gestern").
 * Browser-nativ via Intl.RelativeTimeFormat — kein extra Lib nötig.
 * Für Zeitstempel > 7 Tage faellt das auf das Standard-Datum zurueck (sonst
 * wird "vor 23 Tagen" lesbar genug, aber konkretes Datum hilft mehr).
 */
export function formatRelativeTime(
  date: string | Date | null | undefined,
  locale = LOCALE_DE,
): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = d.getTime() - Date.now();
  const diffSec = Math.round(diffMs / 1000);
  const absSec = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (absSec < 45) return 'gerade eben';
  if (absSec < 60 * 60) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (absSec < 60 * 60 * 24) return rtf.format(Math.round(diffSec / 3600), 'hour');
  if (absSec < 60 * 60 * 24 * 7) return rtf.format(Math.round(diffSec / 86400), 'day');
  // Älter als eine Woche: konkretes Datum hilft mehr als "vor 23 Tagen".
  return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Wandelt eine Byte-Anzahl in eine lesbare Größe um (B / KB / MB). Null → '—'. */
export function humanSize(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
